import React, {useRef} from 'react';
import styles from "./import-file-modal-view.module.scss"
import Dropdown from "../../components/ui-kit/dropdown/dropdown";
import deleteIcon from "../../images/close-16.svg";
import Button from "../../components/ui-kit/button/button";
import Modal from "../modal/modal";
import {useTranslation} from "react-i18next";

export interface ImportFileModalViewProps {
    onClose: () => void;
    selectedCoordinateSystem: number | null;
    onCoordinateSystemChange: (value: number | null) => void;
    selectedFiles: File[];
    removeFile: (file: File) => void;
    error?: string | null;
    handleSubmit: () => void;
    isSubmitDisabled: boolean;
    acceptedFileTypes: string;
    onFilesSelected: (files: File[]) => void;
}

const coordinateSystems = new Map<number, string>([
    [4326, 'WGS 84'],
    [32639, 'WGS 84 / UTM zone 39N'],
    [32640, 'WGS 84 / UTM zone 40N'],
    [23239, 'Clarke UTM Zone 39N'],
    [23240, 'Clarke UTM Zone 40N'],
]);

export const isAllowedExtension = (file: File, allowedExt: string[]): boolean => {
    const allowed = new Set(allowedExt);
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return allowed.has(ext);
};

const ImportFileModalView: React.FC<ImportFileModalViewProps> = ({
    onClose,
    onCoordinateSystemChange,
    selectedFiles,
    removeFile,
    error,
    handleSubmit,
    isSubmitDisabled,
    acceptedFileTypes,
    onFilesSelected,
}) => {
    const { t } = useTranslation()

    const readableTypes = acceptedFileTypes
        .split(',')
        .map(str => str.trim())
        .join(', ');

    const fileInputRef = useRef<HTMLInputElement>(null);

    const openFileDialog = () => fileInputRef.current?.click();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) onFilesSelected(Array.from(e.target.files));
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        onFilesSelected(Array.from(e.dataTransfer.files));
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    return (
        <Modal closeOnOverlayClick={false} onClose={onClose}>
            <div className={styles.modalWrapper}>
                <h2 className={styles.title}>{t('import-file-modal.title')}</h2>
                <Dropdown
                    options={coordinateSystems}
                    onChange={(selectedOption) => {
                        if (typeof selectedOption === 'number' || selectedOption === null) {
                            onCoordinateSystemChange(selectedOption);
                        }
                    }}
                    classes={{
                        dropdown: styles.dropdown,
                        label: styles.dropdownLabel
                    }}
                    title={t('import-file-modal.coordinateSystem')}
                />

                <div
                    className={styles.dropzone}
                    onClick={openFileDialog}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <p>{t('import-file-modal.dropzoneText')}</p>
                    <span>{readableTypes} {t('import-file-modal.fileFormats')}</span>
                    <input
                        ref={fileInputRef}
                        id="fileInput"
                        type="file"
                        accept={acceptedFileTypes}
                        multiple
                        onChange={handleFileChange}
                    />
                </div>
                {selectedFiles.length > 0 && (
                    <div className={styles.fileList}>
                        {selectedFiles.map((file, index) => (
                            <div key={index} className={styles.fileItem}>
                                {file.name}
                                <button
                                    className={styles.removeButton}
                                    onClick={() => removeFile(file)}
                                >
                                    <img src={deleteIcon} alt='Delete'/>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {error && <div className={styles.error}>{error}</div>}
                <div className={styles.groupBtn}>
                    <Button
                        type='withIcon'
                        onClick={handleSubmit}
                        className={styles.submitBtn}
                        disabled={isSubmitDisabled}
                    >
                        {t('import-file-modal.importButton')}
                    </Button>
                    <Button
                        type='outline'
                        className={styles.cancelBtn}
                        onClick={onClose}
                    >
                        {t('import-file-modal.cancelButton')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ImportFileModalView;